import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useSessionAuth } from '../hooks/useSessionAuth';
import { navigateWithinSpa } from '../../lib/spa-navigation';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute
 *
 * Wraps any page that requires an authenticated session. Unauthenticated
 * visitors are redirected to /login with the intended path preserved via
 * the ?redirect= query param so the user is sent back after sign-in.
 *
 * Public routes (/login and all marketing pages) must NOT be wrapped with
 * this component.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useSessionAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      // Navigate to /login, preserving the original path via the ?redirect= param
      const loginUrl = `/login?redirect=${encodeURIComponent(currentPath)}`;
      navigateWithinSpa(loginUrl);
    }
  }, [isAuthenticated, isLoading]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect is in-flight via useEffect — render nothing to avoid flash
  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
